import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

const pillars: Pillar[] = [
  {
    title: 'Eliminate standing privilege',
    description:
      'Default state: zero. Privileges only exist for the duration of an approved task. After the timer, the grant evaporates.',
    bullets: [
      'No default admin groups, no permanent root',
      'Time-bound elevation with hard expiry',
      'Auto-revoke regardless of user behavior',
      'Audit trail on every grant and revoke',
    ],
  },
  {
    title: 'Approval flows the org actually uses',
    description:
      'Tie elevation requests to your existing change management, ticket system, or on-call rotation. Approve from email, mobile, or chat.',
    bullets: [
      'ServiceNow, Jira, and webhook integrations',
      'On-call-aware routing via PagerDuty rotation',
      'Multi-step and break-glass paths',
      'Slack / Teams / email approval surfaces',
    ],
  },
  {
    title: 'Right-sized access',
    description:
      'Grant access to one host, one command, one database, not the whole environment. Least privilege as the default shape.',
    bullets: [
      'Per-host or per-cluster elevation',
      'Command-level scoping for SSH access',
      'Per-table or per-schema database access',
      'Cloud-IAM session credentials with explicit role',
    ],
  },
  {
    title: 'Observable end-to-end',
    description:
      'Every elevation is a first-class event: request, approval, grant, use, revoke. All searchable, all exportable, all tied to the session recording.',
    bullets: [
      'Searchable elevation history per user and asset',
      'Linked to corresponding session recording',
      'Reports for SOC 2, ISO 27001, and PCI-DSS',
      'Anomaly detection on elevation patterns',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Strip standing access',
    description:
      'Move all admin groups into Monopam control. Users keep nothing by default; they request elevation when needed.',
  },
  {
    title: 'Wire approvals',
    description:
      'Map elevation paths to your change-management or ticket system. Define break-glass routes for emergencies.',
  },
  {
    title: 'Elevate, work, expire',
    description:
      'Approvals trigger time-bound grants. Sessions get recorded. Grants expire automatically; no cleanup needed.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'Monopam Gateway',
    description:
      'The gateway that brokers the elevated session.',
    href: '/pam/gateway',
  },
  {
    label: 'Session recording',
    description:
      'Every JIT session is captured and indexed.',
    href: '/pam/session-recording',
  },
  {
    label: 'Zero Trust',
    description:
      'Use case: JIT as a foundation for Zero Trust access.',
    href: '/solutions/zero-trust',
  },
]

export default function JitAccessPage() {
  return (
    <Layout>
      <SolutionMeta
        title="Just-in-time privileged access"
        description="Eliminate standing privilege. Time-bound, approval-driven access with hard expiry. Linked to session recording and audit reports."
        path="/pam/jit-access"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Monopam · Just-in-time access"
          title={
            <>
              Standing privilege:
              <br />
              eliminated.
            </>
          }
          body="Default access is zero. Elevation is requested, approved, time-bound, and revoked automatically. Every grant is tied to the session it enabled, observable end-to-end."
          badges={[
            'Zero standing privilege',
            'Time-bound elevation',
            'Approval flows that fit',
          ]}
        />
        <SolutionPillars
          title={<>The privilege is the exception, not the rule.</>}
          body="JIT access is how mature security teams cut the privileged-account attack surface to a fraction of its size, without slowing engineers down."
          pillars={pillars}
        />
        <ProductHowItWorks
          title={<>From standing privilege to elevation-on-demand.</>}
          steps={steps}
        />
        <SolutionRelated items={related} />
        <FinalCta />
      </main>
    </Layout>
  )
}
